import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Icon, type IconName } from "@/components/common/Icon";
import { NavItemButton } from "@/components/common/NavItemButton";
import { IconButton } from "@/components/common/IconButton";
import { EnvironmentDetailsPanel } from "./EnvironmentDetailsPanel";
import { EnvironmentOtherPanel } from "./EnvironmentOtherPanel";
import { EnvironmentRobotPanel } from "./EnvironmentRobotPanel";
import { EnvironmentSecretsPanel } from "./EnvironmentSecretsPanel";
import { EnvironmentSettingsPanel } from "./EnvironmentSettingsPanel";
import { EnvironmentUsbDevicesPanel } from "./EnvironmentUsbDevicesPanel";
import { EnvironmentVolumesPanel } from "./EnvironmentVolumesPanel";
import { PluginActions } from "./PluginActions";
import type { Environment } from "@/lib/api";
import { isAbortError } from "@/lib/abort-error";
import { formatRelativeDateTime } from "@/lib/format-datetime";
import {
	deleteMarketplace,
	installMarketplacePlugin,
	isInlineMarketplaceError,
	listMarketplacePlugins,
	listRegisteredMarketplaces,
	pluginActionKey,
	pluginIdentity,
	refreshMarketplace,
	setMarketplacePluginEnabled,
	uninstallMarketplacePlugin,
	updateMarketplacePlugin,
	MarketplaceApiError,
	type MarketplaceIssue,
	type MarketplacePlugin,
	type MarketplaceSourceKind,
	type PluginAction,
	type RegisteredMarketplace,
} from "@/lib/marketplace";
import { useHasPermission } from "@/lib/permissions";
import {
	TerminalSessionView,
	type TerminalSessionPhase,
} from "@/components/terminal/TerminalSessionView";
import { useToastStore } from "@/stores/useToastStore";

type SettingsDialogTab =
	| "details"
	| "settings"
	| "usbDevices"
	| "robot"
	| "secrets"
	| "volumes"
	| "terminal"
	| "other"
	| "plugins"
	| "marketplaces";

const TAB_ICONS: Record<SettingsDialogTab, IconName> = {
	details: "info",
	settings: "earth",
	usbDevices: "chip",
	robot: "agent",
	secrets: "key",
	terminal: "terminal",
	other: "settings",
	volumes: "database",
	plugins: "grid",
	marketplaces: "store",
};

const TAB_LABELS: Record<SettingsDialogTab, string> = {
	details: "エージェント詳細",
	settings: "ネットワーク",
	usbDevices: "USB 機器",
	robot: "ロボット",
	secrets: "シークレット",
	terminal: "ターミナル",
	other: "そのほか",
	volumes: "共有ボリューム",
	plugins: "プラグイン一覧",
	marketplaces: "マーケットプレイス",
};

const ALL_TABS: readonly SettingsDialogTab[] = [
	"details",
	"settings",
	"usbDevices",
	"robot",
	"secrets",
	"volumes",
	"plugins",
	"marketplaces",
	"terminal",
	"other",
];

/** デバイスの環境でだけ出すタブ。 */
const RUNNER_ONLY_TABS: ReadonlySet<SettingsDialogTab> = new Set(["usbDevices", "robot"]);

/** エージェント管理の権限がなくても開けるタブ (変更操作を含まない)。 */
const UNMANAGED_TABS: ReadonlySet<SettingsDialogTab> = new Set(["details", "terminal"]);

/** 環境が停止中でも使えるタブ (プラグイン情報を必要としない)。 */
const OFFLINE_TABS: ReadonlySet<SettingsDialogTab> = new Set([
	"details",
	"settings",
	"usbDevices",
	"robot",
	"secrets",
	"volumes",
	"terminal",
	"other",
]);

function observeAsyncFailure(promise: Promise<unknown>, context: string): void {
	void promise.catch((error: unknown) => {
		if (!isAbortError(error)) console.error(context, error);
	});
}

const MODAL_PLACEMENT: React.CSSProperties = {
	margin: "auto",
	boxShadow: "var(--shadow-card)",
	width: "min(960px, calc(100vw - 32px))",
	height: "min(760px, calc(100vh - 32px))",
};

const POPOVER_GAP = 4;
const POPOVER_MARGIN = 16;
const POPOVER_Z_INDEX = 50;
const POPOVER_MAX_WIDTH = 840;
const POPOVER_MAX_HEIGHT = 600;

/**
 * 左端をアンカーに揃えて下へ出す。下に入りきらなければアンカーの上へ出し、上下とも足りなければ
 * 画面内に収まる位置へ寄せる。右にはみ出す分も画面内へ寄せる。
 */
function popoverPlacement(anchor: HTMLElement): React.CSSProperties {
	const rect = anchor.getBoundingClientRect();
	const width = Math.min(POPOVER_MAX_WIDTH, window.innerWidth - POPOVER_MARGIN * 2);
	const height = Math.min(POPOVER_MAX_HEIGHT, window.innerHeight - POPOVER_MARGIN * 2);
	const below = rect.bottom + POPOVER_GAP;
	const above = rect.top - POPOVER_GAP - height;
	const top =
		below + height <= window.innerHeight - POPOVER_MARGIN
			? below
			: above >= POPOVER_MARGIN
				? above
				: window.innerHeight - POPOVER_MARGIN - height;
	return {
		position: "fixed",
		inset: "auto",
		top,
		left: Math.max(POPOVER_MARGIN, Math.min(rect.left, window.innerWidth - POPOVER_MARGIN - width)),
		margin: 0,
		zIndex: POPOVER_Z_INDEX,
		boxShadow: "var(--shadow-popover)",
		width,
		height,
	};
}

interface EnvironmentSettingsDialogProps {
	open: boolean;
	environment: Environment;
	/** プラグイン管理が利用できるか (環境が起動中か)。ネットワークタブは停止中でも使える。 */
	pluginsAvailable: boolean;
	/** 削除の確認ダイアログを開く。 */
	onDelete: () => void;
	/** 起動・停止などの操作中は削除できない。 */
	deleteDisabled: boolean;
	/** 削除の確認を開く前の確認 (反映していない変更の数え直し) の途中か。 */
	deleteLoading?: boolean;
	/** ターミナルを開けるか (環境が稼働中か)。 */
	terminalAvailable: boolean;
	/** 環境 Pod が存在するか。クラウドの環境では、ある間は共有ボリュームのマウント解除ができない。 */
	podExists: boolean;
	onClose: () => void;
	/** 名前の変更やネットワークタブでの保存に成功したら更新後の環境を通知する。 */
	onEnvironmentUpdated: (updated: Environment) => void;
	/** 指定するとモーダルではなく、この要素の下に出るポップオーバーとして開く。 */
	anchor?: HTMLElement | null;
}

const SOURCE_LABELS: Record<MarketplaceSourceKind, string> = {
	relativePath: "マーケットプレイス内パス",
	github: "GitHub",
	url: "URL",
	gitSubdir: "Gitサブディレクトリ",
	npm: "npm",
	archive: "アーカイブ",
	unknown: "不明",
	invalid: "不正",
};

function StatusChip({
	label,
	tone = "neutral",
}: {
	label: string;
	tone?: "neutral" | "good" | "warn" | "bad";
}) {
	const colors = {
		neutral: { background: "var(--surface-2)", color: "var(--fg-2)" },
		good: { background: "var(--success-subtle)", color: "var(--success)" },
		warn: { background: "var(--warning-subtle)", color: "var(--warning)" },
		bad: { background: "var(--danger-subtle)", color: "var(--danger)" },
	}[tone];
	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				borderRadius: "var(--radius-full)",
				padding: "2px 7px",
				fontSize: "var(--text-section)",
				fontWeight: 600,
				...colors,
			}}
		>
			{label}
		</span>
	);
}

function IssueList({ issues, title }: { issues: MarketplaceIssue[]; title: string }) {
	if (issues.length === 0) return null;
	const hasError = issues.some((issue) => issue.severity === "error");
	return (
		<div
			style={{
				padding: 12,
				borderRadius: "var(--radius-3)",
				background: hasError ? "var(--danger-subtle)" : "var(--warning-subtle)",
			}}
		>
			{/* 注意の地は無彩色なので、警告だけのときは見出しの文字とアイコンで注意を示す。 */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 6,
					fontSize: "var(--text-caption)",
					fontWeight: 600,
					color: hasError ? "var(--fg-2)" : "var(--warning)",
					marginBottom: 6,
				}}
			>
				{!hasError && <Icon name="alertTriangle" size={14} color="var(--warning)" />}
				{title}
			</div>
			<ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
				{issues.map((issue, index) => (
					<li
						key={`${issue.path}:${issue.code}:${index}`}
						style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)" }}
					>
						{issue.message}
					</li>
				))}
			</ul>
		</div>
	);
}

function PluginListItem({
	plugin,
	selected,
	disabled,
	onSelect,
}: {
	plugin: MarketplacePlugin;
	selected: boolean;
	disabled: boolean;
	onSelect: () => void;
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			onClick={onSelect}
			data-testid={`plugin-entry-${pluginIdentity(plugin)}`}
			style={{
				width: "100%",
				padding: "12px 14px",
				border: 0,
				borderBottom: "var(--pane-header-border)",
				background: selected ? "var(--row-selected-bg)" : "transparent",
				textAlign: "left",
				fontFamily: "inherit",
				cursor: disabled ? "not-allowed" : "pointer",
				opacity: disabled ? 0.65 : 1,
			}}
		>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<span
					style={{
						flex: 1,
						minWidth: 0,
						fontSize: "var(--text-ui)",
						fontWeight: 600,
						color: "var(--fg-1)",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{plugin.metadata.displayName ?? plugin.metadata.name}
				</span>
				{!plugin.supported && <StatusChip label="未対応" tone="bad" />}
				{plugin.inconsistencies.length > 0 && <StatusChip label="不整合" tone="bad" />}
			</div>
			<div style={{ marginTop: 4, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
				{plugin.marketplace}
				{plugin.metadata.version ? ` · ${plugin.metadata.version}` : ""}
			</div>
			<div style={{ display: "flex", gap: 5, marginTop: 8, flexWrap: "wrap" }}>
				{plugin.installed === true && <StatusChip label="インストール済み" tone="good" />}
				{plugin.enabled === true && <StatusChip label="有効" tone="good" />}
				{plugin.updateAvailable === true && <StatusChip label="更新あり" tone="warn" />}
				{plugin.warnings.length > 0 && (
					<StatusChip label={`警告 ${plugin.warnings.length}`} tone="warn" />
				)}
			</div>
		</button>
	);
}

function PluginDetail({
	plugin,
	actionKey,
	actionError,
	actionNotice,
	onInstall,
	onUninstall,
	onSetEnabled,
	onUpdate,
}: {
	plugin: MarketplacePlugin;
	actionKey: string | null;
	actionError: string | null;
	actionNotice: string | null;
	onInstall: () => void;
	onUninstall: () => void;
	onSetEnabled: (enabled: boolean) => void;
	onUpdate: () => void;
}) {
	const remainingWarnings =
		plugin.source?.supported === false
			? plugin.warnings.filter((issue) => issue.code !== "unsupported_source")
			: plugin.warnings;

	return (
		<div
			style={{ padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}
		>
			<div>
				<div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
					<div style={{ flex: 1, minWidth: 0 }}>
						<h4
							style={{
								margin: 0,
								fontSize: "var(--text-heading)",
								fontWeight: 600,
								color: "var(--fg-1)",
							}}
						>
							{plugin.metadata.displayName ?? plugin.metadata.name}
						</h4>
						<div style={{ marginTop: 4, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
							{plugin.key ?? `${plugin.metadata.name}@${plugin.marketplace}`}
						</div>
					</div>
					{plugin.supported ? (
						<StatusChip label="Izanagi対応" tone="good" />
					) : (
						<StatusChip label="Izanagi未対応" tone="bad" />
					)}
				</div>
				<p
					style={{
						margin: "12px 0 0",
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-body)",
						color: "var(--fg-2)",
					}}
				>
					{plugin.metadata.description ?? "説明はありません。"}
				</p>
			</div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
					gap: 10,
				}}
			>
				<div
					style={{ padding: 12, background: "var(--surface-2)", borderRadius: "var(--radius-3)" }}
				>
					<div style={{ fontSize: "var(--text-section)", color: "var(--fg-3)" }}>ソース</div>
					<div style={{ marginTop: 4, fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>
						{plugin.source ? SOURCE_LABELS[plugin.source.kind] : "未指定"}
					</div>
				</div>
				<div
					style={{ padding: 12, background: "var(--surface-2)", borderRadius: "var(--radius-3)" }}
				>
					<div style={{ fontSize: "var(--text-section)", color: "var(--fg-3)" }}>バージョン</div>
					<div style={{ marginTop: 4, fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>
						{plugin.metadata.version ?? "未指定"}
					</div>
				</div>
				<div
					style={{ padding: 12, background: "var(--surface-2)", borderRadius: "var(--radius-3)" }}
				>
					<div style={{ fontSize: "var(--text-section)", color: "var(--fg-3)" }}>状態</div>
					<div style={{ marginTop: 4, fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>
						{plugin.installed === null
							? "判定不能"
							: plugin.installed
								? plugin.enabled
									? "インストール済み・有効"
									: "インストール済み・無効"
								: "未インストール"}
					</div>
				</div>
			</div>

			<PluginActions
				plugin={plugin}
				actionKey={actionKey}
				actionError={actionError}
				actionNotice={actionNotice}
				onInstall={onInstall}
				onUninstall={onUninstall}
				onSetEnabled={onSetEnabled}
				onUpdate={onUpdate}
			/>

			{plugin.source?.supported === false && (
				<IssueList
					title="未対応のソース"
					issues={[
						{
							path: "$.source",
							code: "unsupported_source",
							message:
								plugin.source.unsupportedReason ??
								"このソース形式は Izanagi v1 では利用できません。",
							severity: "warning",
						},
					]}
				/>
			)}
			<IssueList title="不整合" issues={plugin.inconsistencies} />
			<IssueList title="警告" issues={remainingWarnings} />
		</div>
	);
}

function MarketplaceListItem({
	marketplace,
	actionKey,
	onRefresh,
	onDelete,
}: {
	marketplace: RegisteredMarketplace;
	actionKey: string | null;
	onRefresh: () => void;
	onDelete: () => void;
}) {
	const refreshing = actionKey === `refresh:${marketplace.name}`;
	return (
		<div
			style={{
				padding: 14,
				border: "var(--pane-header-border)",
				borderRadius: "var(--radius-3)",
				background: "var(--surface-1)",
			}}
		>
			<div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div style={{ fontSize: "var(--text-ui)", fontWeight: 600, color: "var(--fg-1)" }}>
						{marketplace.name}
					</div>
					<div style={{ marginTop: 3, fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>
						{marketplace.repository ?? "リポジトリ不明"}
						{marketplace.ref ? ` · ${marketplace.ref}` : ""}
					</div>
				</div>
				<div style={{ display: "flex", gap: 7 }}>
					<ConfirmButton
						label="更新"
						loading={refreshing}
						disabled={actionKey !== null && !refreshing}
						variant="secondary"
						size="sm"
						onClick={onRefresh}
						data-testid={`marketplace-refresh-${marketplace.name}`}
					/>
					<button
						type="button"
						onClick={onDelete}
						disabled={actionKey !== null}
						title="削除"
						data-testid={`marketplace-delete-${marketplace.name}`}
						style={{
							width: 28,
							height: 28,
							display: "grid",
							placeItems: "center",
							border: 0,
							borderRadius: "var(--radius-2)",
							background: "var(--surface-2)",
							color: "var(--danger)",
							cursor: actionKey === null ? "pointer" : "not-allowed",
						}}
					>
						<Icon name="trash" size={13} />
					</button>
				</div>
			</div>
			<div
				style={{
					marginTop: 11,
					display: "grid",
					gridTemplateColumns: "minmax(0, 1fr) auto",
					gap: 12,
					fontSize: "var(--text-section)",
					color: "var(--fg-3)",
				}}
			>
				<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					SHA: {marketplace.gitCommitSha ?? "未解決"}
				</span>
				<span style={{ fontVariantNumeric: "tabular-nums" }}>
					{formatRelativeDateTime(marketplace.lastUpdated)}
				</span>
			</div>
		</div>
	);
}

/**
 * 環境インスタンス設定ダイアログ。起動時設定 (ネットワーク)・共有ボリュームのマウント・環境単位の
 * プラグイン一覧・登録済みマーケットプレイスの管理を提供する。ネットワークと共有ボリュームのタブは環境の
 * 状態に関わらず使えるが、プラグイン系のタブは環境の起動中のみ利用できる。
 */
export function EnvironmentSettingsDialog({
	open,
	environment,
	pluginsAvailable,
	terminalAvailable,
	onDelete,
	deleteDisabled,
	deleteLoading = false,
	podExists,
	onClose,
	onEnvironmentUpdated,
	anchor = null,
}: EnvironmentSettingsDialogProps) {
	const environmentId = environment.id;
	const dialogRef = useRef<HTMLDialogElement>(null);
	const controllerRef = useRef<AbortController | null>(null);
	const actionControllerRef = useRef<AbortController | null>(null);
	const actionInFlightRef = useRef(false);
	const showToast = useToastStore((state) => state.show);
	const [activeTab, setActiveTab] = useState<SettingsDialogTab>("details");
	// ターミナルは一度開いたらダイアログを閉じるまで保ち、タブを移っても実行中のコマンドを止めない。
	// terminalId はダイアログごとに固定なので、開き直しても作業ディレクトリは引き継がれる。
	const [terminalId] = useState(() => crypto.randomUUID());
	const [terminalMounted, setTerminalMounted] = useState(false);
	const [terminalPhase, setTerminalPhase] = useState<TerminalSessionPhase>("prompt");
	const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
	// エージェントが止まるとターミナルは実行状態を伝えずに消えるため、ここで実行中を解く。
	useEffect(() => {
		if (!terminalAvailable) setTerminalPhase("prompt");
	}, [terminalAvailable]);
	// 閉じると PTY ごと実行中のコマンドも中断されるため、実行中のみ確認を挟む。
	const requestClose = () => {
		if (terminalPhase === "running") setCloseConfirmOpen(true);
		else onClose();
	};
	const selectTab = (tab: SettingsDialogTab) => {
		if (tab === "terminal") setTerminalMounted(true);
		setActiveTab(tab);
	};
	const canManageAgents = useHasPermission("manageAgents");
	const visibleTabs = ALL_TABS.filter(
		(tab) =>
			(!RUNNER_ONLY_TABS.has(tab) || !!environment.runner) &&
			(canManageAgents || UNMANAGED_TABS.has(tab)),
	);
	const [plugins, setPlugins] = useState<MarketplacePlugin[]>([]);
	const [pluginIssues, setPluginIssues] = useState<MarketplaceIssue[]>([]);
	const [marketplaces, setMarketplaces] = useState<RegisteredMarketplace[]>([]);
	const [marketplaceIssues, setMarketplaceIssues] = useState<MarketplaceIssue[]>([]);
	const [selectedPluginId, setSelectedPluginId] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [marketplaceActionError, setMarketplaceActionError] = useState<string | null>(null);
	const [pluginActionError, setPluginActionError] = useState<string | null>(null);
	const [pluginActionNotice, setPluginActionNotice] = useState<string | null>(null);
	const [actionKey, setActionKey] = useState<string | null>(null);
	const [pendingDelete, setPendingDelete] = useState<RegisteredMarketplace | null>(null);
	const [pendingUninstall, setPendingUninstall] = useState<MarketplacePlugin | null>(null);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;
		if (open && !dialog.open) {
			if (anchor) dialog.show();
			else dialog.showModal();
		}
		if (!open && dialog.open) dialog.close();
	}, [open, anchor]);

	const requestCloseRef = useRef(requestClose);
	requestCloseRef.current = requestClose;
	useEffect(() => {
		if (!open || !anchor) return;
		// 確認ダイアログなど別のモーダルが開いている間は、そちらの操作で閉じないようにする。
		const otherModalOpen = () =>
			Array.from(document.querySelectorAll("dialog[open]")).some(
				(element) => element !== dialogRef.current && element.matches(":modal"),
			);
		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target as Node;
			if (dialogRef.current?.contains(target) || anchor.contains(target) || otherModalOpen())
				return;
			requestCloseRef.current();
		};
		// 名前の編集や確認ダイアログなど、中の UI が処理済み (preventDefault) の Escape では閉じない。
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape" || event.defaultPrevented || otherModalOpen()) return;
			requestCloseRef.current();
		};
		// 開いている間のアンカー押下も閉じる操作として扱い、ターミナル実行中の確認を通す。
		// 呼び出し側の onClick (開く操作) へ届かないよう、React のルートより手前で止める。
		const handleAnchorClick = (event: MouseEvent) => {
			event.stopPropagation();
			requestCloseRef.current();
		};
		document.addEventListener("pointerdown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		anchor.addEventListener("click", handleAnchorClick);
		return () => {
			document.removeEventListener("pointerdown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
			anchor.removeEventListener("click", handleAnchorClick);
		};
	}, [open, anchor]);

	const loadData = useCallback(async (): Promise<boolean> => {
		controllerRef.current?.abort();
		const controller = new AbortController();
		controllerRef.current = controller;
		setLoading(true);
		setLoadError(null);
		try {
			const [catalog, registered] = await Promise.all([
				listMarketplacePlugins(environmentId, controller.signal),
				listRegisteredMarketplaces(environmentId, controller.signal),
			]);
			if (controllerRef.current !== controller || controller.signal.aborted) return false;
			setPlugins(catalog.plugins);
			setPluginIssues(catalog.issues);
			setMarketplaces(registered.marketplaces);
			setMarketplaceIssues(registered.issues);
		} catch (error) {
			if (isAbortError(error) || controllerRef.current !== controller || controller.signal.aborted)
				return false;
			if (isInlineMarketplaceError(error)) {
				setLoadError(error.message);
			} else {
				showToast({
					type: "error",
					message:
						error instanceof MarketplaceApiError
							? error.message
							: "プラグイン情報を取得できませんでした。通信状態を確認して再度お試しください。",
				});
			}
			throw error;
		} finally {
			if (controllerRef.current === controller) {
				controllerRef.current = null;
				setLoading(false);
			}
		}
		return true;
	}, [environmentId, showToast]);

	const resetActionState = useCallback(() => {
		actionControllerRef.current?.abort();
		actionControllerRef.current = null;
		actionInFlightRef.current = false;
		setActionKey(null);
	}, []);

	useEffect(() => {
		if (!open) {
			controllerRef.current?.abort();
			resetActionState();
			setTerminalMounted(false);
			setTerminalPhase("prompt");
			setCloseConfirmOpen(false);
			return;
		}
		resetActionState();
		setActiveTab("details");
		setPlugins([]);
		setPluginIssues([]);
		setMarketplaces([]);
		setMarketplaceIssues([]);
		setSelectedPluginId(null);
		setMarketplaceActionError(null);
		setPluginActionError(null);
		setPluginActionNotice(null);
		setPendingDelete(null);
		setPendingUninstall(null);
		// プラグイン情報は環境の起動中のみ取得できる (停止中はネットワークタブだけが機能する)。
		if (pluginsAvailable) observeAsyncFailure(loadData(), "プラグイン情報の取得に失敗しました");
		return () => {
			controllerRef.current?.abort();
			actionControllerRef.current?.abort();
		};
	}, [loadData, open, pluginsAvailable, resetActionState]);

	useEffect(() => {
		if (plugins.length === 0) {
			setSelectedPluginId(null);
			return;
		}
		if (!plugins.some((plugin) => pluginIdentity(plugin) === selectedPluginId)) {
			setSelectedPluginId(pluginIdentity(plugins[0]));
		}
	}, [plugins, selectedPluginId]);

	const selectedPlugin = useMemo(
		() => plugins.find((plugin) => pluginIdentity(plugin) === selectedPluginId) ?? null,
		[plugins, selectedPluginId],
	);

	useEffect(() => {
		setPluginActionError(null);
		setPluginActionNotice(null);
	}, [selectedPluginId]);

	const runAction = async <T,>(
		key: string,
		operation: (signal: AbortSignal) => Promise<T>,
		setInlineError: (message: string | null) => void,
		fallback: string,
		reportError: boolean,
		onSuccess?: (result: T) => void,
	) => {
		if (actionInFlightRef.current) return;
		const controller = new AbortController();
		actionControllerRef.current = controller;
		actionInFlightRef.current = true;
		setActionKey(key);
		setInlineError(null);
		try {
			try {
				const result = await operation(controller.signal);
				if (actionControllerRef.current !== controller || controller.signal.aborted) return;
				onSuccess?.(result);
			} catch (error) {
				if (
					isAbortError(error) ||
					actionControllerRef.current !== controller ||
					controller.signal.aborted
				)
					return;
				if (reportError) {
					if (isInlineMarketplaceError(error)) {
						setInlineError(error.message);
					} else {
						showToast({
							type: "error",
							message: error instanceof MarketplaceApiError ? error.message : fallback,
						});
					}
				}
				throw error;
			}
			await loadData();
		} finally {
			if (actionControllerRef.current === controller) {
				actionControllerRef.current = null;
				actionInFlightRef.current = false;
				setActionKey(null);
			}
		}
	};

	const runMarketplaceAction = (
		key: string,
		operation: (signal: AbortSignal) => Promise<unknown>,
		reportError = true,
	) =>
		runAction(
			key,
			operation,
			setMarketplaceActionError,
			"マーケットプレイスと通信できませんでした。再度お試しください。",
			reportError,
		);

	const runPluginAction = (
		plugin: MarketplacePlugin,
		action: PluginAction,
		operation: (signal: AbortSignal) => Promise<string>,
		reportError = true,
	) => {
		setPluginActionNotice(null);
		return runAction(
			pluginActionKey(plugin, action),
			operation,
			setPluginActionError,
			"プラグイン操作に失敗しました。通信状態を確認して再度お試しください。",
			reportError,
			setPluginActionNotice,
		);
	};

	if (!open) return null;

	return (
		<>
			{anchor && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						zIndex: POPOVER_Z_INDEX - 1,
						background: "var(--scrim-light)",
					}}
				/>
			)}
			<dialog
				ref={dialogRef}
				onCancel={(event) => {
					event.preventDefault();
					requestClose();
				}}
				data-testid="plugin-marketplace-dialog"
				className="backdrop:bg-scrim"
				style={{
					padding: 0,
					border: 0,
					borderRadius: "var(--radius-4)",
					background: "var(--surface-1)",
					overflow: "hidden",
					...(anchor ? popoverPlacement(anchor) : MODAL_PLACEMENT),
				}}
			>
				<div style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 12,
							padding: "14px 20px",
							borderBottom: "var(--pane-header-border)",
						}}
					>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: 32,
								height: 32,
								flexShrink: 0,
								borderRadius: "var(--radius-3)",
								background: "var(--surface-2)",
								color: "var(--fg-2)",
							}}
						>
							<Icon name="agent" size={18} />
						</span>
						<h3
							style={{
								flex: 1,
								minWidth: 0,
								margin: 0,
								fontSize: "var(--text-title)",
								fontWeight: 600,
								color: "var(--fg-1)",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
							title={environment.name}
							data-tooltip-if-truncated
						>
							{environment.name}
						</h3>
						<IconButton
							icon="x"
							title="閉じる"
							onClick={requestClose}
							data-testid="environment-settings-dialog-close"
						/>
					</div>

					<div style={{ flex: 1, minHeight: 0, display: "flex" }}>
						<nav
							style={{
								flex: "none",
								width: 168,
								display: "flex",
								flexDirection: "column",
								gap: 4,
								padding: 8,
								borderRight: "var(--pane-header-border)",
							}}
						>
							{visibleTabs.map((tab) => (
								<NavItemButton
									key={tab}
									icon={TAB_ICONS[tab]}
									label={TAB_LABELS[tab]}
									compact
									selected={activeTab === tab}
									onClick={() => selectTab(tab)}
									testId={`environment-settings-dialog-tab-${tab}`}
								/>
							))}
						</nav>
						<div
							style={{
								flex: 1,
								minWidth: 0,
								minHeight: 0,
								display: "flex",
								flexDirection: "column",
							}}
						>
							{activeTab !== "details" && activeTab !== "terminal" && (
								<h4
									data-testid="environment-settings-dialog-tab-heading"
									style={{
										margin: 0,
										padding: "20px 24px 0",
										fontSize: "var(--text-heading)",
										fontWeight: 600,
										lineHeight: "var(--leading-normal)",
										color: "var(--fg-1)",
									}}
								>
									{TAB_LABELS[activeTab]}
								</h4>
							)}
							{loadError && !OFFLINE_TABS.has(activeTab) && (
								<div
									style={{
										display: "flex",
										flexWrap: "wrap",
										alignItems: "center",
										gap: 10,
										margin: 16,
										padding: 12,
										borderRadius: "var(--radius-3)",
										background: "var(--danger-subtle)",
										color: "var(--danger)",
										fontSize: "var(--text-caption)",
									}}
								>
									<span>{loadError}</span>
									<ConfirmButton
										label="再試行"
										loading={false}
										variant="outline"
										size="sm"
										onClick={() =>
											observeAsyncFailure(loadData(), "プラグイン情報の再取得に失敗しました")
										}
									/>
								</div>
							)}

							{activeTab === "details" ? (
								<EnvironmentDetailsPanel
									environment={environment}
									podExists={podExists}
									onUpdated={onEnvironmentUpdated}
								/>
							) : activeTab === "settings" ? (
								<EnvironmentSettingsPanel
									environment={environment}
									onSaved={onEnvironmentUpdated}
								/>
							) : activeTab === "usbDevices" ? (
								<EnvironmentUsbDevicesPanel environmentId={environment.id} />
							) : activeTab === "robot" ? (
								<EnvironmentRobotPanel environmentId={environment.id} />
							) : activeTab === "other" ? (
								<EnvironmentOtherPanel
									environmentId={environment.id}
									onDelete={onDelete}
									deleteDisabled={deleteDisabled}
									deleteLoading={deleteLoading}
								/>
							) : activeTab === "terminal" ? (
								!terminalAvailable && (
									<div
										data-testid="environment-terminal-unavailable"
										style={{
											flex: 1,
											display: "grid",
											placeItems: "center",
											color: "var(--fg-3)",
											fontSize: "var(--text-caption)",
										}}
									>
										ターミナルはエージェントの起動中のみ利用できます。
									</div>
								)
							) : activeTab === "secrets" ? (
								<EnvironmentSecretsPanel
									environmentId={environment.id}
									onDevice={environment.runner !== null}
								/>
							) : activeTab === "volumes" ? (
								<EnvironmentVolumesPanel
									environment={environment}
									podExists={podExists}
									onSaved={onEnvironmentUpdated}
								/>
							) : !pluginsAvailable ? (
								<div
									data-testid="environment-settings-dialog-unavailable"
									style={{
										flex: 1,
										display: "grid",
										placeItems: "center",
										color: "var(--fg-3)",
										fontSize: "var(--text-caption)",
									}}
								>
									プラグインの管理はエージェントの起動中のみ利用できます。エージェントを起動してから再度開いてください。
								</div>
							) : loading && plugins.length === 0 && marketplaces.length === 0 ? (
								<div
									style={{
										flex: 1,
										display: "grid",
										placeItems: "center",
										color: "var(--fg-3)",
										fontSize: "var(--text-caption)",
									}}
								>
									プラグイン情報を読み込んでいます…
								</div>
							) : activeTab === "plugins" ? (
								<div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[280px_minmax(0,1fr)] md:overflow-hidden">
									<div className="min-h-[180px] overflow-visible border-b border-line-1 md:min-h-0 md:overflow-y-auto md:border-r md:border-b-0">
										{pluginIssues.length > 0 && (
											<div style={{ padding: 12 }}>
												<IssueList title="カタログの不整合" issues={pluginIssues} />
											</div>
										)}
										{plugins.length === 0 ? (
											<div
												style={{
													padding: 24,
													fontSize: "var(--text-caption)",
													color: "var(--fg-3)",
													textAlign: "center",
												}}
											>
												表示できるプラグインはありません
											</div>
										) : (
											plugins.map((plugin) => (
												<PluginListItem
													key={pluginIdentity(plugin)}
													plugin={plugin}
													selected={pluginIdentity(plugin) === selectedPluginId}
													disabled={actionKey !== null}
													onSelect={() => setSelectedPluginId(pluginIdentity(plugin))}
												/>
											))
										)}
									</div>
									{selectedPlugin ? (
										<PluginDetail
											plugin={selectedPlugin}
											actionKey={actionKey}
											actionError={pluginActionError}
											actionNotice={pluginActionNotice}
											onInstall={() =>
												observeAsyncFailure(
													runPluginAction(selectedPlugin, "install", async (signal) => {
														await installMarketplacePlugin(
															environmentId,
															selectedPlugin.marketplace,
															selectedPlugin.metadata.name,
															signal,
														);
														return "インストールしました。次のジョブから有効になります。";
													}),
													"プラグインのインストールに失敗しました",
												)
											}
											onUninstall={() => setPendingUninstall(selectedPlugin)}
											onSetEnabled={(enabled) =>
												observeAsyncFailure(
													runPluginAction(
														selectedPlugin,
														enabled ? "enable" : "disable",
														async (signal) => {
															await setMarketplacePluginEnabled(
																environmentId,
																selectedPlugin.marketplace,
																selectedPlugin.metadata.name,
																enabled,
																signal,
															);
															return enabled
																? "有効にしました。次のジョブから有効になります。"
																: "無効にしました。次のジョブから無効になります。";
														},
													),
													"プラグインの有効状態の変更に失敗しました",
												)
											}
											onUpdate={() =>
												observeAsyncFailure(
													runPluginAction(selectedPlugin, "update", async (signal) => {
														const result = await updateMarketplacePlugin(
															environmentId,
															selectedPlugin.marketplace,
															selectedPlugin.metadata.name,
															signal,
														);
														return result.updated
															? "更新しました。次のジョブから更新内容が反映されます。"
															: "このプラグインは既に最新です。";
													}),
													"プラグインの更新に失敗しました",
												)
											}
										/>
									) : (
										<div
											style={{
												display: "grid",
												placeItems: "center",
												color: "var(--fg-3)",
												fontSize: "var(--text-caption)",
											}}
										>
											左の一覧からプラグインを選択してください。
										</div>
									)}
								</div>
							) : (
								<div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 20 }}>
									{marketplaceActionError && (
										<div
											style={{
												marginTop: 12,
												padding: 10,
												borderRadius: "var(--radius-3)",
												background: "var(--danger-subtle)",
												color: "var(--danger)",
												fontSize: "var(--text-caption)",
											}}
										>
											{marketplaceActionError}
										</div>
									)}
									{marketplaceIssues.length > 0 && (
										<div style={{ marginTop: 12 }}>
											<IssueList title="状態の不整合" issues={marketplaceIssues} />
										</div>
									)}
									<div style={{ marginTop: 16, display: "grid", gap: 10 }}>
										{marketplaces.length === 0 ? (
											<div
												style={{
													padding: 24,
													fontSize: "var(--text-caption)",
													color: "var(--fg-3)",
													textAlign: "center",
												}}
											>
												登録済みのマーケットプレイスはありません
											</div>
										) : (
											marketplaces.map((marketplace) => (
												<MarketplaceListItem
													key={marketplace.name}
													marketplace={marketplace}
													actionKey={actionKey}
													onRefresh={() =>
														observeAsyncFailure(
															runMarketplaceAction(`refresh:${marketplace.name}`, (signal) =>
																refreshMarketplace(environmentId, marketplace.name, signal),
															),
															"マーケットプレイスの更新に失敗しました",
														)
													}
													onDelete={() => setPendingDelete(marketplace)}
												/>
											))
										)}
									</div>
								</div>
							)}
							{terminalMounted && terminalAvailable && (
								<TerminalSessionView
									terminalId={terminalId}
									active={activeTab === "terminal"}
									visible={open && activeTab === "terminal"}
									chatId={null}
									environmentId={environment.id}
									refreshFilesOnExit
									onPhaseChange={(_terminalId, nextPhase) => setTerminalPhase(nextPhase)}
								/>
							)}
						</div>
					</div>
				</div>
				<ConfirmDialog
					open={closeConfirmOpen}
					title="ダイアログを閉じますか?"
					message="ターミナルでコマンドを実行中です。閉じると実行中のコマンドは中断されます。"
					confirmLabel="閉じる"
					onConfirm={() => {
						setCloseConfirmOpen(false);
						onClose();
						return Promise.resolve();
					}}
					onCancel={() => setCloseConfirmOpen(false)}
				/>
			</dialog>

			<ConfirmDialog
				open={pendingDelete !== null}
				title="マーケットプレイスを削除"
				message={`「${pendingDelete?.name ?? ""}」を削除します。インストール済みプラグインが残っている場合は削除できません。`}
				confirmLabel="削除"
				onCancel={() => setPendingDelete(null)}
				onConfirm={async () => {
					if (!pendingDelete) return;
					const marketplaceName = pendingDelete.name;
					await runMarketplaceAction(
						`delete:${marketplaceName}`,
						async (signal) => {
							await deleteMarketplace(environmentId, marketplaceName, signal);
							signal.throwIfAborted();
							setMarketplaces((current) =>
								current.filter((marketplace) => marketplace.name !== marketplaceName),
							);
							setPendingDelete(null);
						},
						false,
					);
				}}
			/>
			<ConfirmDialog
				open={pendingUninstall !== null}
				title="プラグインをアンインストール"
				message={`「${pendingUninstall?.metadata.displayName ?? pendingUninstall?.metadata.name ?? ""}」をアンインストールします。再び利用するにはインストールが必要です。`}
				confirmLabel="アンインストール"
				onCancel={() => setPendingUninstall(null)}
				onConfirm={async () => {
					if (!pendingUninstall) return;
					const plugin = pendingUninstall;
					await runPluginAction(
						plugin,
						"uninstall",
						async (signal) => {
							await uninstallMarketplacePlugin(
								environmentId,
								plugin.marketplace,
								plugin.metadata.name,
								signal,
							);
							signal.throwIfAborted();
							setPendingUninstall(null);
							return "アンインストールしました。次のジョブから利用されなくなります。";
						},
						false,
					);
				}}
			/>
		</>
	);
}
